import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FolderAddOutlined,
  PlusOutlined,
  UploadOutlined
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Input, Modal, Select, Spin, Table, Tooltip, message } from "antd";
import { deleteSprText, fetchSprTextList, importSprText } from "./spr-text-api";
import SprTextDrawer from "./spr-text-drawer";
import {
  SPR_TEXT_TABS,
  buildSprTextExport,
  buildTextTree,
  getLawFlag,
  normalizeSprText,
  parseSprTextImport
} from "./spr-text-utils";
import "./spr-text.css";

const DEFAULT_VID = 0;

const isAllowedVid = (value) => SPR_TEXT_TABS.some((t) => t.vid === value);

const filtersFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  const vidRaw = Number(src.get("vid"));
  const vid = Number.isFinite(vidRaw) && isAllowedVid(vidRaw) ? vidRaw : DEFAULT_VID;
  return { vid };
};

const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  const vid =
    Number.isFinite(filters?.vid) && isAllowedVid(filters.vid) ? filters.vid : DEFAULT_VID;
  params.set("vid", String(vid));
  return params;
};

/**
 * Справочник текстовых шаблонов (Delphi TFrmSprText / SPR_TEXT).
 */
const SprText = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const vid = urlFilters.vid;

  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 1) === 1;

  const [rows, setRows] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerIdUpper, setDrawerIdUpper] = useState(0);
  const [drawerIsNew, setDrawerIsNew] = useState(false);
  const [drawerHideKod, setDrawerHideKod] = useState(false);

  const listReqRef = useRef(0);
  const fileInputRef = useRef(null);

  const loadList = useCallback(() => {
    const requestId = ++listReqRef.current;
    setLoadingList(true);
    setError("");
    return fetchSprTextList(vid)
      .then((data) => {
        if (requestId !== listReqRef.current) return;
        const list = Array.isArray(data)
          ? data.map(normalizeSprText).filter((r) => r.idText != null)
          : [];
        setRows(list);
        setSelectedId((prev) => {
          if (prev != null && list.some((r) => r.idText === prev)) return prev;
          return null;
        });
        setExpandedKeys([]);
      })
      .catch((e) => {
        if (requestId !== listReqRef.current) return;
        setError(e.message || "Не удалось загрузить шаблоны");
        setRows([]);
      })
      .finally(() => {
        if (requestId === listReqRef.current) setLoadingList(false);
      });
  }, [vid]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const next = filtersToSearchParams({ vid });
    if (searchParams.toString() === next.toString()) return;
    setSearchParams(next, { replace: true });
  }, [vid, searchParams, setSearchParams]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.idText === selectedId) || null,
    [rows, selectedId]
  );

  const filteredRows = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return rows;
    const matchIds = new Set();
    const byId = new Map(rows.map((r) => [r.idText, r]));
    rows.forEach((r) => {
      const hay = `${r.kod || ""} ${r.name || ""}`.toLowerCase();
      if (!hay.includes(q)) return;
      let cur = r;
      while (cur) {
        matchIds.add(cur.idText);
        cur = cur.idUpper > 0 ? byId.get(cur.idUpper) : null;
      }
    });
    return rows.filter((r) => matchIds.has(r.idText));
  }, [rows, nameFilter]);

  const treeData = useMemo(() => buildTextTree(filteredRows), [filteredRows]);

  useEffect(() => {
    if (!nameFilter.trim()) return;
    setExpandedKeys(filteredRows.map((r) => String(r.idText)));
  }, [nameFilter, filteredRows]);

  const openCreateSibling = () => {
    if (!canEdit) return;
    const idUpper = selectedRow ? selectedRow.idUpper || 0 : 0;
    setDrawerIsNew(true);
    setDrawerId(null);
    setDrawerIdUpper(idUpper);
    setDrawerHideKod(false);
    setDrawerOpen(true);
  };

  const openCreateChild = () => {
    if (!canEdit || !selectedRow) return;
    setDrawerIsNew(true);
    setDrawerId(null);
    setDrawerIdUpper(selectedRow.idText);
    setDrawerHideKod(vid === 1);
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    if (row?.idText == null) return;
    setSelectedId(row.idText);
    setDrawerIsNew(false);
    setDrawerId(row.idText);
    setDrawerIdUpper(row.idUpper || 0);
    setDrawerHideKod(vid === 1);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const onDrawerSaved = (saved) => {
    const row = normalizeSprText(saved);
    loadList().then(() => {
      if (row.idText != null) {
        setSelectedId(row.idText);
        setDrawerId(row.idText);
        setDrawerIsNew(false);
        setDrawerHideKod(vid === 1);
      }
    });
  };

  const onDel = () => {
    if (!selectedRow || !canEdit) return;
    Modal.confirm({
      title: "Удалить?",
      content: "Выбранный шаблон будет удалён.",
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        deleteSprText(vid, selectedRow.idText)
          .then(() => {
            message.success("Удалено");
            if (drawerId === selectedRow.idText) closeDrawer();
            const parentId = selectedRow.idUpper || null;
            setSelectedId(parentId);
            return loadList();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const labelOf = (v) => SPR_TEXT_TABS.find((t) => t.vid === v)?.label || String(v);

  const onExport = () => {
    fetchSprTextList(vid)
      .then((data) => {
        const list = Array.isArray(data)
          ? data.map(normalizeSprText).filter((r) => r.idText != null)
          : [];
        const payload = buildSprTextExport(vid, list);
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
          type: "application/json"
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `spr-text-${vid}-${(payload.label || "razdel").replace(/[\\/:*?"<>|]/g, "_")}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        message.success(`Выгружено: ${list.length}`);
      })
      .catch((e) => message.error(e.message || "Не удалось выгрузить"));
  };

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    let parsed;
    try {
      parsed = parseSprTextImport(await file.text());
    } catch (e) {
      message.error(e.message || "Не удалось прочитать файл");
      return;
    }
    if (Number.isFinite(parsed.vid) && parsed.vid !== vid) {
      message.error(
        `Файл содержит раздел «${labelOf(parsed.vid)}», а открыт «${labelOf(vid)}»`
      );
      return;
    }
    Modal.confirm({
      title: "Загрузить шаблоны из файла?",
      content: `Раздел «${labelOf(vid)}» будет полностью очищен (сейчас: ${rows.length}), затем загружено записей: ${parsed.items.length}.`,
      okText: "Загрузить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        importSprText({ vid, items: parsed.items })
          .then((res) => {
            message.success(`Загружено: ${res?.count ?? parsed.items.length}`);
            closeDrawer();
            setSelectedId(null);
            return loadList();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось загрузить");
            return Promise.reject(e);
          })
    });
  };

  const columns = useMemo(() => {
    const cols = [];
    if (vid === 0) {
      cols.push({
        title: "Код",
        dataIndex: "kod",
        key: "kod",
        width: 100,
        ellipsis: true
      });
    }
    cols.push({
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      render: (name) => <span className="spr-text__name">{name || ""}</span>
    });
    return cols;
  }, [vid]);

  const vidOptions = SPR_TEXT_TABS.map((t) => ({ value: t.vid, label: t.label }));

  const onVidChange = (nextVid) => {
    if (!isAllowedVid(nextVid) || nextVid === vid) return;
    setSelectedId(null);
    setNameFilter("");
    closeDrawer();
    setSearchParams(filtersToSearchParams({ vid: nextVid }), { replace: true });
  };

  return (
    <div className="spr-text">
      {error ? (
        <Alert className="spr-text__error" type="error" showIcon message={error} />
      ) : null}

      <div className="spr-text__body">
        <div className="spr-text__list">
          <div className="spr-text__toolbar">
            <div className="spr-text__extra-actions">
              <Tooltip title="Добавить">
                <span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!canEdit}
                    aria-label="Добавить"
                    onClick={openCreateSibling}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Доб. в...">
                <span>
                  <Button
                    icon={<FolderAddOutlined />}
                    disabled={!canEdit || !selectedRow}
                    aria-label="Добавить вложенный"
                    onClick={openCreateChild}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Изменить">
                <span>
                  <Button
                    icon={<EditOutlined />}
                    disabled={!selectedRow}
                    aria-label="Изменить"
                    onClick={() => openEdit(selectedRow)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Удалить">
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!canEdit || !selectedRow}
                    aria-label="Удалить"
                    onClick={onDel}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Выгрузить в файл">
                <span>
                  <Button
                    icon={<DownloadOutlined />}
                    disabled={!rows.length || loadingList}
                    aria-label="Выгрузить в файл"
                    onClick={onExport}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Загрузить из файла">
                <span>
                  <Button
                    icon={<UploadOutlined />}
                    disabled={!canEdit || loadingList}
                    aria-label="Загрузить из файла"
                    onClick={() => fileInputRef.current?.click()}
                  />
                </span>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                style={{ display: "none" }}
                onChange={importFile}
              />
            </div>
            <Select
              className="spr-text__vid"
              value={vid}
              options={vidOptions}
              onChange={onVidChange}
            />
            <Input
              allowClear
              variant="borderless"
              className="spr-text__filter"
              placeholder="Поиск"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <div className="spr-text__list-body">
            {loadingList && !rows.length ? (
              <div className="spr-text__center">
                <Spin />
              </div>
            ) : (
              <Table
                className="spr-text__table"
                size="small"
                rowKey={(r) => String(r.idText)}
                pagination={false}
                columns={columns}
                dataSource={treeData}
                loading={loadingList}
                expandable={{
                  expandedRowKeys: expandedKeys,
                  onExpandedRowsChange: (keys) => setExpandedKeys(keys.map(String))
                }}
                rowClassName={(row) =>
                  row.idText === selectedId ? "spr-text__row--selected" : ""
                }
                onRow={(row) => ({
                  onClick: () => setSelectedId(row.idText),
                  onDoubleClick: () => openEdit(row)
                })}
                locale={{
                  emptyText: rows.length ? "Ничего не найдено" : "Нет шаблонов"
                }}
              />
            )}
          </div>
        </div>
      </div>

      <SprTextDrawer
        open={drawerOpen}
        vid={vid}
        idText={drawerId}
        idUpper={drawerIdUpper}
        isNew={drawerIsNew}
        hideKod={drawerHideKod}
        canEdit={canEdit}
        onClose={closeDrawer}
        onSaved={onDrawerSaved}
      />
    </div>
  );
};

export default SprText;
